/**
 * SandboxSetupChecklist — the five-step "Setting up your sandbox" progression,
 * shared by the standalone SetupTakeover (Overview tab body) and the Flow
 * Designer's SandboxSetupPopover so both render identical progress from one
 * source of truth.
 *
 * Steps map off the backing instance's raw status (`instanceStatus` /
 * `EnvironmentInstanceStatus`). "Run health checks" (step 4) is active once the
 * instance is `running` but before the first health-check result lands.
 */
import React from 'react';
import { cn } from '@chatsdet/ui';
import type { EnvironmentInstanceStatus } from '../../../hooks/useEnvironmentInstances';

export const STEPS = [
  'Provisioning',
  'Bootstrap VM',
  'Install dependencies',
  'Deploy app',
  'Run health checks',
];

// Only `pending`, `provisioning`, `deploying`, and `running` (pre-first-check)
// drive a live view — callers gate on the setting-up window. The map stays
// exhaustive for type-safety; setup is already complete by `converting`, and
// terminal/stopping states fall through to the final step.
export const STATUS_TO_STEP: Record<EnvironmentInstanceStatus, number> = {
  pending: 0,
  provisioning: 1,
  // Backend has no distinct "install dependencies" status — dependency
  // hydration happens inside the 'deploying' window alongside the compose
  // up. We jump to "Deploy app" (index 3) so step 2 reads as done.
  deploying: 3,
  running: 4,
  converting: 5,
  stopping: 5,
  terminated: 5,
  failed: 5,
};

/** Resolve the active step index from a raw status string (unknown → step 0). */
export function statusToStep(status: EnvironmentInstanceStatus | string | null): number {
  if (status && status in STATUS_TO_STEP) {
    return STATUS_TO_STEP[status as EnvironmentInstanceStatus];
  }
  return 0;
}

export interface SandboxSetupChecklistProps {
  status: EnvironmentInstanceStatus | string | null;
  /** "md" for the takeover card, "sm" for the tighter popover. */
  size?: 'sm' | 'md';
  className?: string;
}

export function SandboxSetupChecklist({
  status,
  size = 'md',
  className,
}: SandboxSetupChecklistProps) {
  const stepIdx = statusToStep(status);
  const sm = size === 'sm';

  return (
    <ol className={cn('flex flex-col', sm ? 'gap-2' : 'gap-3', className)}>
      {STEPS.map((label, i) => {
        const isDone = i < stepIdx;
        const isActive = i === stepIdx;
        const badgeSize = sm ? 'h-5 w-5 text-[10px]' : 'h-6 w-6 text-xs';
        return (
          <li key={label} className="flex items-center gap-3">
            <span
              className={cn(
                'inline-flex items-center justify-center rounded-full font-bold',
                badgeSize,
                isDone
                  ? 'bg-emerald-500 text-white'
                  : isActive
                  ? 'bg-primary/10 text-primary ring-2 ring-primary/40'
                  : 'border border-border text-muted-foreground font-medium'
              )}
            >
              {isDone ? '✓' : i + 1}
            </span>
            <span
              className={cn(
                sm ? 'text-xs' : 'text-sm',
                isDone
                  ? 'text-muted-foreground line-through'
                  : isActive
                  ? 'font-medium text-foreground inline-flex items-center gap-2'
                  : 'text-muted-foreground'
              )}
            >
              {label}
              {isActive && (
                <span
                  aria-hidden
                  className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse"
                />
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

SandboxSetupChecklist.displayName = 'SandboxSetupChecklist';
